import { useEffect, useState } from "react";
import { FormModal, Input, Select } from "@marekl/adminer/components";
import {
  DEVICE_FUNCTION_LABELS,
  stationLabel,
  useCreatePairing,
  type DeviceFunction,
  type JudgingTable,
  type NewPairing,
  type Station,
} from "../../../lib/queries/devices";
import type { ID } from "../../../lib/id";
import { useAuth } from "../../../auth/useAuth";
import { isAdmin } from "../../../auth/queries";

interface PairingModalProps {
  competitionId: ID;
  tables: JudgingTable[];
  open: boolean;
  onClose: () => void;
  /** The code made, and what it pairs, for the page to show. */
  onCreated: (issued: {
    id: ID;
    code: string;
    expiresAtMs: number;
    target: string;
  }) => void;
}

const FUNCTIONS: DeviceFunction[] = [
  "moderator",
  "head_judge",
  "checkin",
  "it",
];

// „Přidat zařízení": what the device will stand for, and its name. The
// code is made in the browser; the page shows it as a QR and as text.
export function PairingModal({
  competitionId,
  tables,
  open,
  onClose,
  onCreated,
}: PairingModalProps) {
  const create = useCreatePairing(competitionId);
  // A debug device acts as anyone: only an administrator pairs one (the
  // server refuses everybody else too).
  const { user } = useAuth();
  const functions: DeviceFunction[] = isAdmin(user)
    ? [...FUNCTIONS, "debug"]
    : FUNCTIONS;
  const [target, setTarget] = useState("");
  const [label, setLabel] = useState("");

  useEffect(() => {
    if (open) {
      setTarget("");
      setLabel("");
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when the dialog opens
  }, [open]);

  if (!open) return null;

  // Every place each table has: its most demanding block sizes it.
  const stations = (t: JudgingTable): Station[] => [
    ...Array.from({ length: t.plus_judges }, (_, i) => ({
      table: t.table,
      seat_kind: "plus" as const,
      seat_position: i + 1,
    })),
    ...Array.from({ length: t.tech_judges }, (_, i) => ({
      table: t.table,
      seat_kind: "tech" as const,
      seat_position: i + 1,
    })),
  ];
  const seated = tables.filter((t) => stations(t).length > 0);

  const describe = (
    value: string,
  ): { body: NewPairing; text: string } | null => {
    const [kind, ...rest] = value.split(":");
    if (kind === "f") {
      const fn = rest[0] as DeviceFunction;
      return {
        body: { function: fn, label },
        text: DEVICE_FUNCTION_LABELS[fn],
      };
    }
    if (kind === "s") {
      const [table, seatKind, position] = rest;
      const station: Station = {
        table: Number(table),
        seat_kind: seatKind as "plus" | "tech",
        seat_position: Number(position),
      };
      return { body: { station, label }, text: stationLabel(station) };
    }
    return null;
  };

  return (
    <FormModal
      open
      title="Přidat zařízení"
      submitLabel="Vystavit kód"
      error={create.error}
      busy={create.isPending}
      canSubmit={target !== ""}
      onSubmit={() => {
        const d = describe(target);
        if (!d) return;
        create.mutate(d.body, {
          onSuccess: (res) => {
            onCreated({
              id: res.id,
              code: res.code,
              expiresAtMs: res.expires_at_ms,
              target: label.trim() ? `${d.text} · ${label.trim()}` : d.text,
            });
            onClose();
          },
        });
      }}
      onClose={onClose}
    >
      <Select
        label="Zařízení bude"
        value={target}
        hint={
          seated.length === 0
            ? "Místa u stolů poroty tu zatím nejsou: objeví se, až bude mít startovní listina bloky disciplín."
            : "Tablet porotce patří místu u stolu, ne porotě ani člověku: poroty se u stolu střídají, tablet zůstává. Nové zařízení na místě odvolá to dosavadní."
        }
        onChange={(e) => setTarget(e.target.value)}
      >
        <option value="" disabled>
          Vyberte
        </option>
        <optgroup label="Funkce">
          {functions.map((f) => (
            <option key={f} value={`f:${f}`}>
              {DEVICE_FUNCTION_LABELS[f]}
            </option>
          ))}
        </optgroup>
        {seated.map((t) => (
          <optgroup key={t.table} label={`Stůl ${t.table}`}>
            {stations(t).map((s) => (
              <option
                key={`${s.seat_kind}-${s.seat_position}`}
                value={`s:${s.table}:${s.seat_kind}:${s.seat_position}`}
              >
                {stationLabel(s)}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>
      <Input
        label="Název zařízení"
        value={label}
        placeholder="Např. Tablet 3"
        hint="Podle štítku na zařízení, ať ho v seznamu poznáte."
        onChange={(e) => setLabel(e.target.value)}
      />
    </FormModal>
  );
}
